import type { ButtonHTMLAttributes, ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { signal } from './signal';

// ------------------------------------------------------------------ navigation

export const route = signal(location.pathname + location.search);

export function navigate(to: string, replace = false) {
	if (replace) history.replaceState(null, '', to);
	else history.pushState(null, '', to);
	route.set(to);
	window.scrollTo(0, 0);
}
addEventListener('popstate', () => route.set(location.pathname + location.search));

export function Link(p: { href: string; class?: string; children: ComponentChildren; title?: string }) {
	return (
		<a
			href={p.href}
			class={p.class}
			title={p.title}
			onClick={(e) => {
				if (e.metaKey || e.ctrlKey || e.shiftKey) return;
				e.preventDefault();
				navigate(p.href);
			}}
		>
			{p.children}
		</a>
	);
}

// ------------------------------------------------------------------ toasts

export interface Toast {
	id: number;
	text: string;
	kind: 'info' | 'warn' | 'error' | 'ok' | 'trait';
}
export const toasts = signal<Toast[]>([]);
let toastId = 0;
export function toast(text: string, kind: Toast['kind'] = 'info', ms = 4500) {
	const t = { id: ++toastId, text, kind };
	toasts.set([...toasts.get(), t]);
	setTimeout(() => toasts.set(toasts.get().filter((x) => x.id !== t.id)), ms);
}
export function Toasts() {
	const list = toasts.use();
	return (
		<div class="toasts" role="status" aria-live="polite">
			{list.map((t) => (
				<div key={t.id} class={`toast toast-${t.kind}`}>
					{t.text}
				</div>
			))}
		</div>
	);
}

export function errorText(e: unknown): string {
	return e instanceof Error ? e.message : String(e);
}

/** Run an async action, toasting errors, tracking busy state. */
export function useAction<A extends unknown[]>(fn: (...a: A) => Promise<unknown>) {
	const [busy, setBusy] = useState(false);
	const run = async (...a: A) => {
		setBusy(true);
		try {
			await fn(...a);
		} catch (e) {
			toast(errorText(e), 'error');
		} finally {
			setBusy(false);
		}
	};
	return [run, busy] as const;
}

/** Load data from an async function; reload() re-runs it. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[] = []) {
	const [data, setData] = useState<T | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [n, setN] = useState(0);
	useEffect(() => {
		let live = true;
		setError(null);
		fn().then(
			(d) => live && setData(d),
			(e) => live && setError(errorText(e)),
		);
		return () => {
			live = false;
		};
	}, [...deps, n]);
	return { data, error, reload: () => setN((x) => x + 1), setData };
}

// ------------------------------------------------------------------ primitives

export function Button(p: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' | 'danger' | 'quiet'; busy?: boolean; small?: boolean }) {
	const { variant = 'primary', busy, small, class: cls, children, disabled, ...rest } = p;
	return (
		<button class={`btn btn-${variant}${small ? ' btn-sm' : ''} ${cls ?? ''}`} disabled={disabled || busy} {...rest}>
			{busy ? <span class="spinner" aria-hidden="true" /> : null}
			{children}
		</button>
	);
}

export function Card(p: { children: ComponentChildren; class?: string; title?: ComponentChildren; actions?: ComponentChildren }) {
	return (
		<section class={`card ${p.class ?? ''}`}>
			{p.title || p.actions ? (
				<header class="card-head">
					{p.title ? <h2>{p.title}</h2> : <span />}
					{p.actions ? <div class="card-actions">{p.actions}</div> : null}
				</header>
			) : null}
			{p.children}
		</section>
	);
}

export function Field(p: { label: string; hint?: string; children: ComponentChildren }) {
	return (
		<label class="field">
			<span class="field-label">{p.label}</span>
			{p.children}
			{p.hint ? <span class="field-hint">{p.hint}</span> : null}
		</label>
	);
}

export function Modal(p: { title: string; onClose?: () => void; children: ComponentChildren; wide?: boolean }) {
	useEffect(() => {
		const k = (e: KeyboardEvent) => e.key === 'Escape' && p.onClose?.();
		addEventListener('keydown', k);
		return () => removeEventListener('keydown', k);
	}, []);
	return (
		<div class="modal-back" onClick={(e) => e.target === e.currentTarget && p.onClose?.()}>
			<div class={`modal ${p.wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={p.title}>
				<header class="modal-head">
					<h2>{p.title}</h2>
					{p.onClose ? (
						<button class="icon-btn" onClick={p.onClose} aria-label="Close">
							×
						</button>
					) : null}
				</header>
				<div class="modal-body">{p.children}</div>
			</div>
		</div>
	);
}

export function Tabs<T extends string>(p: { tabs: Array<{ id: T; label: ComponentChildren; badge?: number }>; value: T; onChange: (t: T) => void; class?: string }) {
	return (
		<nav class={`tabs ${p.class ?? ''}`} role="tablist">
			{p.tabs.map((t) => (
				<button key={t.id} role="tab" aria-selected={p.value === t.id} class={`tab ${p.value === t.id ? 'tab-on' : ''}`} onClick={() => p.onChange(t.id)}>
					{t.label}
					{t.badge ? <span class="badge">{t.badge}</span> : null}
				</button>
			))}
		</nav>
	);
}

export function Empty(p: { children: ComponentChildren }) {
	return <p class="empty">{p.children}</p>;
}

export function Loading() {
	return (
		<div class="loading">
			<span class="spinner" /> Loading…
		</div>
	);
}

export function Pill(p: { children: ComponentChildren; tone?: 'ok' | 'warn' | 'bad' | 'accent' | 'muted' }) {
	return <span class={`pill pill-${p.tone ?? 'muted'}`}>{p.children}</span>;
}

export function Countdown(p: { deadline: number; skew?: number; total?: number }) {
	const [now, setNow] = useState(Date.now());
	useEffect(() => {
		const t = setInterval(() => setNow(Date.now()), 250);
		return () => clearInterval(t);
	}, []);
	const left = Math.max(0, Math.ceil((p.deadline - (now + (p.skew ?? 0))) / 1000));
	const pct = p.total ? Math.max(0, Math.min(100, (left / p.total) * 100)) : 100;
	return (
		<div class={`countdown ${left <= 10 ? 'countdown-low' : ''}`} aria-label={`${left} seconds left`}>
			<div class="countdown-bar" style={{ width: `${pct}%` }} />
			<span>{left}s</span>
		</div>
	);
}

export function timeAgo(ts: number): string {
	const s = Math.round((Date.now() - ts) / 1000);
	if (s < 60) return `${s}s ago`;
	if (s < 3600) return `${Math.round(s / 60)}m ago`;
	if (s < 86400) return `${Math.round(s / 3600)}h ago`;
	return new Date(ts).toLocaleDateString();
}

export function money(cents: number): string {
	return cents === 0 ? 'Free' : `$${(cents / 100).toFixed(2)}`;
}
